import React from "react";
import { useTranslation } from "react-i18next";
import type { ChatbotStarredImage } from "../../services/chatbot.service";

const LINE_APP_ICON_SRC = "/assets/common/images/line-app-icon.png";

interface StarredGalleryProps {
  items: ChatbotStarredImage[];
  isLoading: boolean;
  showSendToLine: boolean;
  onBack: () => void;
  onOpen: (item: ChatbotStarredImage) => void;
  onDownload: (item: ChatbotStarredImage) => void;
  onSendToLine: (item: ChatbotStarredImage) => void;
  onJumpToContext: (item: ChatbotStarredImage) => void;
  onUnstar: (item: ChatbotStarredImage) => void;
}

export const StarredGallery = ({
  items,
  isLoading,
  showSendToLine,
  onBack,
  onOpen,
  onDownload,
  onSendToLine,
  onJumpToContext,
  onUnstar,
}: StarredGalleryProps): React.ReactElement => {
  const { t } = useTranslation();

  return (
    <div className="restaurant-chatbot-starred-view">
      <div className="restaurant-chatbot-session-view-header">
        <button
          className="btn btn-icon btn-outline-primary"
          type="button"
          onClick={onBack}
          title={t("chatbot_back_to_chat", { defaultValue: "Back to chat" })}
        >
          <i className="ti ti-arrow-left" />
        </button>
        <span>{t("chatbot_starred", { defaultValue: "Starred" })}</span>
      </div>

      <div className="restaurant-chatbot-starred-content">
        {isLoading ? (
          <div className="restaurant-chatbot-session-state">
            {t("chatbot_starred_loading", { defaultValue: "Loading starred posters..." })}
          </div>
        ) : items.length ? (
          <div className="restaurant-chatbot-starred-grid">
            {items.map((item) => (
              <div className="restaurant-chatbot-starred-card" key={item.id}>
                <button
                  className="restaurant-chatbot-starred-preview"
                  type="button"
                  onClick={() => onOpen(item)}
                  title={item.title || t("chatbot_generated_image", { defaultValue: "Generated image" })}
                >
                  <img
                    src={item.image_url}
                    alt={item.title || t("chatbot_generated_image", { defaultValue: "Generated image" })}
                    loading="lazy"
                  />
                </button>
                <div className="restaurant-chatbot-starred-meta">
                  <div className="restaurant-chatbot-starred-title">
                    {item.title || t("chatbot_generated_image", { defaultValue: "Generated image" })}
                  </div>
                  <div className="restaurant-chatbot-starred-session">
                    {item.session_title || t("chatbot_sessions", { defaultValue: "History" })}
                  </div>
                  <div className="restaurant-chatbot-starred-date">
                    {new Date(item.starred_at).toLocaleString()}
                  </div>
                </div>
                <div className="restaurant-chatbot-starred-actions">
                  <button
                    className="restaurant-chatbot-copy-btn restaurant-chatbot-inline-image-action"
                    type="button"
                    onClick={() => onJumpToContext(item)}
                    aria-label={t("chatbot_go_to_context", { defaultValue: "Go to chat context" })}
                    data-tooltip={t("chatbot_go_to_context", { defaultValue: "Go to chat context" })}
                  >
                    <i className="ti ti-history" />
                  </button>
                  <button
                    className="restaurant-chatbot-copy-btn restaurant-chatbot-inline-image-action"
                    type="button"
                    onClick={() => onDownload(item)}
                    aria-label={t("chatbot_download_image", { defaultValue: "Download image" })}
                    data-tooltip={t("chatbot_download_image", { defaultValue: "Download image" })}
                  >
                    <i className="ti ti-download" />
                  </button>
                  {showSendToLine ? (
                    <button
                      className="restaurant-chatbot-copy-btn restaurant-chatbot-inline-image-action"
                      type="button"
                      onClick={() => onSendToLine(item)}
                      aria-label={t("chatbot_send_to_line", { defaultValue: "Send to LINE" })}
                      data-tooltip={t("chatbot_send_to_line", { defaultValue: "Send to LINE" })}
                    >
                      <img className="restaurant-chatbot-line-icon" src={LINE_APP_ICON_SRC} alt="LINE" />
                    </button>
                  ) : null}
                  <button
                    className="restaurant-chatbot-copy-btn restaurant-chatbot-inline-image-action active"
                    type="button"
                    onClick={() => onUnstar(item)}
                    aria-label={t("chatbot_unstar_image", { defaultValue: "Remove from starred" })}
                    data-tooltip={t("chatbot_unstar_image", { defaultValue: "Remove from starred" })}
                  >
                    <i className="ti ti-star-filled" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="restaurant-chatbot-session-state">
            {t("chatbot_starred_empty", { defaultValue: "No starred posters yet." })}
          </div>
        )}
      </div>
    </div>
  );
};
